import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  RefreshCw,
  Search,
  ScrollText,
  X,
} from "lucide-react";

import {
  fetchAdminAuditEvents,
} from "../../lib/admin/adminAudit";


const AREAS = [
  ["all", "All"],
  ["profile", "Profile"],
  ["support", "Support"],
  ["notifications", "Notifications"],
  ["followups", "Follow-ups"],
  ["payments", "Payments"],
];


function formatDateTime(
  value
) {
  if (!value) {
    return "—";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "—";
  }

  return date.toLocaleString(
    "en-ZA",
    {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  );
}


function areaLabel(
  value
) {
  const match =
    AREAS.find(
      ([key]) =>
        key === value
    );

  return (
    match?.[1] ||
    value ||
    "Other"
  );
}


export default function AdminAudit() {
  const [
    rows,
    setRows,
  ] =
    useState([]);

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    error,
    setError,
  ] =
    useState("");

  const [
    area,
    setArea,
  ] =
    useState("all");

  const [
    search,
    setSearch,
  ] =
    useState("");

  const [
    selected,
    setSelected,
  ] =
    useState(null);


  const loadRows =
    useCallback(
      async () => {
        try {
          setLoading(
            true
          );

          setError("");

          const data =
            await fetchAdminAuditEvents(
              {
                area,

                limit:
                  500,
              }
            );

          setRows(data);
        } catch (
          loadError
        ) {
          console.error(
            "Admin audit load failed:",
            loadError
          );

          setError(
            loadError?.message ||
              "Audit events could not be loaded."
          );
        } finally {
          setLoading(
            false
          );
        }
      },
      [area]
    );


  useEffect(() => {
    loadRows();
  }, [loadRows]);


  const filtered =
    useMemo(() => {
      const query =
        search
          .trim()
          .toLowerCase();

      if (!query) {
        return rows;
      }

      return rows.filter(
        (row) =>
          [
            row.area,
            row.action,
            row.result,
            row.actor_name,
            row.target_name,
            row.target_id,
            row.summary,
          ]
            .map(
              (value) =>
                String(
                  value || ""
                ).toLowerCase()
            )
            .some(
              (value) =>
                value.includes(
                  query
                )
            )
      );
    }, [
      rows,
      search,
    ]);


  return (
    <>
      <header className="mb-7">
        <p className="text-xs font-bold uppercase tracking-wider text-[#FF914D]">
          Admin · Audit
        </p>

        <h1 className="mt-2 text-[34px] font-bold tracking-[-0.03em] text-[#333333] md:text-[42px]">
          Audit
        </h1>

        <p className="mt-2 max-w-4xl text-sm leading-relaxed text-[#757575] md:text-base">
          Review real recorded activity
          from profile changes,
          technical support,
          notifications, Admin
          follow-ups and payment events.
        </p>
      </header>


      <section className="mb-5 rounded-[24px] border border-[#EFEFEF] bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 xl:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#BCBCBC]" />

            <input
              value={
                search
              }
              onChange={(
                event
              ) =>
                setSearch(
                  event
                    .target
                    .value
                )
              }
              placeholder="Search audit events..."
              className="w-full rounded-full border border-[#E5E5E5] py-3 pl-11 pr-4 text-sm"
            />
          </div>


          <select
            value={
              area
            }
            onChange={(
              event
            ) =>
              setArea(
                event
                  .target
                  .value
              )
            }
            className="rounded-full border border-[#E5E5E5] bg-white px-4 py-3 text-sm font-bold"
          >
            {AREAS.map(
              ([
                key,
                label,
              ]) => (
                <option
                  key={key}
                  value={key}
                >
                  {label}
                </option>
              )
            )}
          </select>


          <button
            type="button"
            onClick={
              loadRows
            }
            disabled={
              loading
            }
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FF914D] px-5 py-3 text-sm font-bold text-white"
          >
            <RefreshCw
              className={[
                "h-4 w-4",
                loading
                  ? "animate-spin"
                  : "",
              ].join(" ")}
            />

            Refresh
          </button>
        </div>
      </section>


      {error && (
        <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
          {error}
        </div>
      )}


      <section className="overflow-hidden rounded-[24px] border border-[#EFEFEF] bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-[1500px] w-full text-left text-sm">
            <thead className="bg-[#FFF4EC] text-xs uppercase text-[#757575]">
              <tr>
                <th className="px-4 py-4">
                  When
                </th>

                <th className="px-4 py-4">
                  Area
                </th>

                <th className="px-4 py-4">
                  Actor
                </th>

                <th className="px-4 py-4">
                  Action
                </th>

                <th className="px-4 py-4">
                  Target
                </th>

                <th className="px-4 py-4">
                  Result
                </th>

                <th className="px-4 py-4">
                  Summary
                </th>

                <th className="px-4 py-4">
                  Details
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-12 text-center text-[#757575]"
                  >
                    Loading audit
                    events…
                  </td>
                </tr>
              ) : filtered.length ===
                0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-12 text-center text-[#757575]"
                  >
                    No audit events
                    found.
                  </td>
                </tr>
              ) : (
                filtered.map(
                  (
                    row
                  ) => (
                    <tr
                      key={
                        row.event_id
                      }
                      className="border-t border-[#EFEFEF] align-top"
                    >
                      <td className="whitespace-nowrap px-4 py-4">
                        {formatDateTime(
                          row.created_at
                        )}
                      </td>

                      <td className="px-4 py-4 font-bold text-[#FF914D]">
                        {areaLabel(
                          row.area
                        )}
                      </td>

                      <td className="px-4 py-4 font-semibold">
                        {row.actor_name ||
                          "System"}
                      </td>

                      <td className="px-4 py-4 font-bold">
                        {row.action ||
                          "—"}
                      </td>

                      <td className="px-4 py-4">
                        <p className="font-semibold">
                          {row.target_name ||
                            "—"}
                        </p>

                        <p className="mt-1 max-w-[260px] truncate text-xs text-[#757575]">
                          {row.target_id ||
                            "—"}
                        </p>
                      </td>

                      <td className="px-4 py-4">
                        {row.result ||
                          "—"}
                      </td>

                      <td className="max-w-[360px] px-4 py-4 text-xs leading-relaxed">
                        {row.summary ||
                          "—"}
                      </td>

                      <td className="px-4 py-4">
                        <button
                          type="button"
                          onClick={() =>
                            setSelected(
                              row
                            )
                          }
                          className="rounded-full border border-[#FFE1CF] px-3 py-2 text-xs font-black text-[#FF914D]"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  )
                )
              )}
            </tbody>
          </table>
        </div>
      </section>


      {selected && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/45 px-4 py-6"
          onMouseDown={(
            event
          ) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              setSelected(
                null
              );
            }
          }}
        >
          <div className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-[28px] bg-white shadow-2xl">
            <header className="flex items-start justify-between border-b border-[#EFEFEF] p-5">
              <div className="flex items-center gap-3">
                <ScrollText className="h-5 w-5 text-[#FF914D]" />

                <div>
                  <p className="font-black">
                    Audit details
                  </p>

                  <p className="text-xs text-[#757575]">
                    {selected.event_id}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() =>
                  setSelected(
                    null
                  )
                }
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            <div className="p-5">
              <pre className="overflow-auto whitespace-pre-wrap rounded-2xl bg-[#FAFAFA] p-4 text-xs leading-relaxed">
                {JSON.stringify(
                  selected.details ||
                    {},
                  null,
                  2
                )}
              </pre>
            </div>
          </div>
        </div>
      )}
    </>
  );
}